import { Button, Pagination, SearchBar, Table } from "@ab-inbev-maz/compass";
import EmptyTable from "./EmptyTable";
import { useState, useMemo } from "react";
import { useReportsStore } from '../../stores/reportsStore';
import { useAlertStore } from '../../stores/alertStore';

interface TableviewProps {
  datos: any[];
  columnas: any[];
  title?: string;
  currentPage?: number;
  totalPages?: number;
  pageSize?: number;
  totalCount?: number;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  showReprocessButton?: boolean;
  showDownloadButton?: boolean;
  onReprocess?: (selectedIds: (string | number)[]) => Promise<void>;
  onDelete?: (rows: any[]) => void;
  onDownload?: (selectedIds: (string | number)[]) => void;
  rowDisabled?: (row: any) => boolean;
  showDeleteButton?: boolean;
}

const Tableview = ({
  datos = [],
  columnas,
  title,
  currentPage = 1,
  pageSize = 25,
  totalCount = 0,
  onPageChange,
  onPageSizeChange,
  searchValue = "",
  onSearchChange,
  showReprocessButton,
  showDownloadButton = true,
  onReprocess,
  rowDisabled,
  showDeleteButton,
  onDelete,
  onDownload,
}: TableviewProps) => {
  // Estado local para selección de filas (solo ids habilitados)
  const [selectedRows, setSelectedRows] = useState<Set<string | number>>(new Set());
  const processComboWithError = useReportsStore(state => state.processComboWithError);
  const { showAlert } = useAlertStore();
  const enabledRowIds = useMemo(() => {
    return datos
      .filter(row => row && !row.disabled && (row.id !== undefined || row.key !== undefined || row.index !== undefined))
      .map(row => row.id ?? row.key ?? row.index);
  }, [datos]);

  const dataWithChecked = useMemo(() => {
    return datos
      .filter(row => row && typeof row === 'object') // Filter out undefined/null
      .map(row => {
        const rowId = row.id ?? row.key ?? row.index;
        const isChecked = selectedRows.has(rowId);
        const isDisabled = rowDisabled ? rowDisabled(row) : (row.disabled ?? false);

        return {
          ...row,
          checked: isChecked,
          disabled: isDisabled
        };
      });
  }, [datos, selectedRows, rowDisabled]);

  const handleCheckAll = (checked: boolean) => {
    setSelectedRows(checked ? new Set(enabledRowIds) : new Set());
  };
  const handleCheckRow = (checked: boolean, rowId: string | number) => {
    setSelectedRows(prev => {
      const newSet = new Set(prev);
      checked ? newSet.add(rowId) : newSet.delete(rowId);
      return newSet;
    });
  };
  const handlePageChangeWrapper = (page: number) => onPageChange?.(page);

  return (
    <section className="flex items-center justify-between p-4 gap-6">
      <Table
        title={title}
        columns={columnas}
        data={dataWithChecked}
        striped
        selection
        empty={<EmptyTable message={`No hay ${title?.toLowerCase() || 'datos'} disponibles`} icon="validation/alert-octagon" />}
        header={
          <div className="flex items-center justify-between gap-8">
            <div className="flex items-center gap-4">
              <SearchBar
                id="search-bar"
                name="Buscar"
                variant="default"
                leftIcon="search"
                className="w-full min-w-[320px] rounded-md"
                placeholder="Buscar..."
                value={searchValue}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => onSearchChange?.(e.target.value)}
                onClear={() => onSearchChange?.("")}
              />
              {
                showDeleteButton && (
                  <Button
                    label="Eliminar"
                    rightIcon="system/trash"
                    variant="secondary"
                    className="cursor-pointer"
                    disabled={selectedRows.size === 0}
                    onClick={onDelete?.bind(null, Array.from(selectedRows))}
                  />
                )
              }
              {
                showDownloadButton && (
                  <Button
                    label="Descargar"
                    rightIcon="arrow/download"
                    variant="primary"
                    className="cursor-pointer"
                    onClick={() => onDownload && onDownload(Array.from(selectedRows))}
                  />
                )
              }
              {showReprocessButton && (
                <Button
                  label="Reprocesar"
                  onClick={async () => {
                    if (selectedRows.size === 0) return;

                    if (onReprocess) {
                      // Usar la función personalizada de reprocesamiento
                      try {
                        const selectedIds = Array.from(selectedRows);
                        await onReprocess(selectedIds);
                        setSelectedRows(new Set()); // Limpiar selección después de reprocesar
                      } catch (error) {
                        console.error('Error al reprocesar:', error);
                      }
                    } else {
                      // Lógica por defecto para errores individuales (combos)
                      const combos = Array.from(selectedRows)
                        .map(rowId => {
                          const row = datos.find(r => (r.id ?? r.key ?? r.index) === rowId);
                          if (!row || row.disabled || (typeof rowDisabled === 'function' && rowDisabled(row))) return null;
                          return row.combo_id || (row.data && row.data.combo_id);
                        })
                        .filter(Boolean)
                        .map(String);
                      try {
                        await processComboWithError(combos);
                        showAlert({
                          text: 'Combos reprocesados correctamente',
                          status: 'success',
                          variant: 'simple',
                          close: true
                        });
                        setSelectedRows(new Set()); // Limpiar selección después de reprocesar
                      } catch (error) {
                        console.error('Error al reprocesar combos:', error);
                      }
                    }
                  }}
                  variant="secondary"
                  rightIcon={'arrow/repeat'}
                  className="cursor-pointer"
                  disabled={selectedRows.size === 0}
                />
              )}
            </div>
          </div>
        }
        footer={
          <div className="flex items-center justify-between gap-4">
            <Pagination
              className="flex-row! !flex-nowrap"
              page={currentPage}
              total={totalCount}
              size={pageSize}
              sizes={[
                { label: 'Filas por página: 25', value: 25 },
                { label: 'Filas por página: 50', value: 50 },
                { label: 'Filas por página: 100', value: 100 },
              ]}
              onChangePage={handlePageChangeWrapper}
              onChangeSize={onPageSizeChange}
            />
          </div>
        }
        onCheckAll={handleCheckAll}
        onCheckRow={handleCheckRow}
      />
    </section>
  );
};

export default Tableview;
